/**
 * Auto-retry policy for failed tasks.
 *
 * Lifecycle:
 *   1. TasksView WS error handler calls `handleError(task)`.
 *   2. The composable checks the per-task toggle and the global config.
 *      - Disabled → no-op (existing manual retry still works).
 *      - Enabled  → push a 5s toast asking the user to confirm.
 *      - Auto-yes after 5s OR the user clicks "Retry now".
 *   3. If the retry succeeds, clear state. If it fails again, schedule the
 *      next attempt after `backoffSeconds` (up to `maxRetries` total).
 *   4. The user can cancel a pending retry at any time; that also opts the
 *      task out of automatic retries for its current error cycle.
 *
 * Persistence:
 *   - Global config in `localStorage` under key `afd-auto-retry`.
 *   - Per-task opt-out in `localStorage` under `afd-auto-retry:skip` (Set of gids).
 *
 * The store is module-level so every component that calls `useAutoRetry()`
 * sees the same state — opening the detail panel for an errored task
 * immediately shows its live retry countdown.
 */
import { computed, reactive, ref } from 'vue'
import type { ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'
import { retryTask as apiRetry, type TaskInfo } from '@/api/tasks'
import { useToast } from '@/composables/useToast'

export interface AutoRetryConfig {
  /** master toggle */
  enabled: boolean
  /** total retry attempts per error cycle (1..5) */
  maxRetries: number
  /** delay between retries, seconds (5..600) */
  backoffSeconds: number
  /** delay before auto-accepting the confirmation prompt (1..30s) */
  confirmWindowSeconds: number
}

export type RetryTaskPhase =
  | 'idle'
  | 'awaiting-confirm'
  | 'backoff'
  | 'in-flight'
  | 'exhausted'
  | 'cancelled'

export interface RetryTaskState {
  gid: string
  /** human-friendly display name (best-effort) */
  name: string
  /** attempts so far in this cycle (0 = first error, no retries yet) */
  attempts: number
  phase: RetryTaskPhase
  /** ms epoch when the next attempt fires (0 = none) */
  nextAttemptAt: number
  /** toast id of the active confirm prompt, for cancellation */
  confirmToastId: string | null
  /** when the cycle started */
  startedAt: number
  /** last error message we surfaced */
  lastError: string
  /** opt-out flag set by the user mid-cycle */
  optedOut: boolean
}

const STORAGE_KEY = 'afd-auto-retry'
const SKIP_KEY = 'afd-auto-retry:skip'

const DEFAULTS: AutoRetryConfig = {
  enabled: false,
  maxRetries: 3,
  backoffSeconds: 30,
  confirmWindowSeconds: 5,
}

const RANGES = {
  maxRetries: { min: 1, max: 5 },
  backoffSeconds: { min: 5, max: 600 },
  confirmWindowSeconds: { min: 1, max: 30 },
} as const

function safeLocalStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function clampInt(v: unknown, fallback: number, min: number, max: number): number {
  const n = typeof v === 'number' ? v : Number(v)
  if (!Number.isFinite(n)) return fallback
  const i = Math.round(n)
  if (i < min) return min
  if (i > max) return max
  return i
}

function loadConfig(): AutoRetryConfig {
  const ls = safeLocalStorage()
  if (!ls) return { ...DEFAULTS }
  const raw = ls.getItem(STORAGE_KEY)
  if (!raw) return { ...DEFAULTS }
  try {
    const obj = JSON.parse(raw) as Partial<AutoRetryConfig>
    return {
      enabled: typeof obj.enabled === 'boolean' ? obj.enabled : DEFAULTS.enabled,
      maxRetries: clampInt(obj.maxRetries, DEFAULTS.maxRetries, RANGES.maxRetries.min, RANGES.maxRetries.max),
      backoffSeconds: clampInt(obj.backoffSeconds, DEFAULTS.backoffSeconds, RANGES.backoffSeconds.min, RANGES.backoffSeconds.max),
      confirmWindowSeconds: clampInt(
        obj.confirmWindowSeconds,
        DEFAULTS.confirmWindowSeconds,
        RANGES.confirmWindowSeconds.min,
        RANGES.confirmWindowSeconds.max,
      ),
    }
  } catch {
    return { ...DEFAULTS }
  }
}

function loadSkipSet(): Set<string> {
  const ls = safeLocalStorage()
  if (!ls) return new Set()
  const raw = ls.getItem(SKIP_KEY)
  if (!raw) return new Set()
  try {
    const arr = JSON.parse(raw) as unknown
    if (!Array.isArray(arr)) return new Set()
    return new Set(arr.filter((x): x is string => typeof x === 'string'))
  } catch {
    return new Set()
  }
}

function saveSkipSet(set: Set<string>): void {
  const ls = safeLocalStorage()
  if (!ls) return
  try {
    ls.setItem(SKIP_KEY, JSON.stringify([...set]))
  } catch {
    /* quota / private mode — ignore */
  }
}

// --- Module-level singleton state ---
const config = reactive<AutoRetryConfig>(loadConfig())
const skipSet = reactive<Set<string>>(new Set(loadSkipSet()))
const states = reactive<Map<string, RetryTaskState>>(new Map())
const nowTick = ref<number>(Date.now())

// Single 1s ticker driving the countdown UIs and triggering scheduled retries.
let tickerHandle: number | null = null
const activeTimers = new Map<string, number>()

function persistConfig(): void {
  const ls = safeLocalStorage()
  if (!ls) return
  try {
    ls.setItem(STORAGE_KEY, JSON.stringify({ ...config }))
  } catch {
    /* ignore */
  }
}

function ensureTicker(): void {
  if (tickerHandle !== null) return
  tickerHandle = window.setInterval(() => {
    nowTick.value = Date.now()
  }, 1000)
}

function clearActiveTimer(gid: string): void {
  const h = activeTimers.get(gid)
  if (h !== undefined) {
    window.clearTimeout(h)
    activeTimers.delete(gid)
  }
}

function clearState(gid: string): void {
  clearActiveTimer(gid)
  const st = states.get(gid)
  if (st?.confirmToastId) {
    try {
      useToast().dismiss(st.confirmToastId)
    } catch {
      /* composable may not be available in tests */
    }
  }
  states.delete(gid)
}

function ensureState(gid: string, name: string): RetryTaskState {
  let st = states.get(gid)
  if (!st) {
    st = {
      gid,
      name,
      attempts: 0,
      phase: 'idle',
      nextAttemptAt: 0,
      confirmToastId: null,
      startedAt: Date.now(),
      lastError: '',
      optedOut: false,
    }
    states.set(gid, st)
  } else if (name && name !== st.name) {
    // Refresh display name if the caller has a better one.
    st.name = name
  }
  return st
}

function persistSkip(): void {
  saveSkipSet(new Set([...skipSet]))
}

export interface UseAutoRetryApi {
  /** Reactive current config; mutating via updateConfig persists. */
  config: AutoRetryConfig
  /** Live states keyed by gid. */
  states: Map<string, RetryTaskState>
  /** Active retry tasks (phase !== 'idle'). */
  active: ComputedRef<RetryTaskState[]>
  /** Tick reference (changes every 1s) so components can recompute countdowns. */
  nowTick: ComputedRef<number>
  /** Set of gids the user opted out for this error cycle. */
  skipSet: Set<string>
  /** Update the persisted config; clamps to safe ranges. */
  updateConfig(patch: Partial<AutoRetryConfig>): void
  /**
   * Entry point called by the WS error handler. Decides whether to:
   *  - ignore (disabled, opted-out, already in flight, etc.)
   *  - schedule an immediate confirmation toast
   */
  handleError(task: Pick<TaskInfo, 'gid' | 'name'> & Partial<TaskInfo>): void
  /** User clicked the manual "Retry now" button on a pending prompt. */
  retryNow(gid: string): Promise<void>
  /** Cancel any pending retry for this task and opt it out for the cycle. */
  cancelRetry(gid: string): void
  /** Manual override: disable auto-retry for a single task this cycle. */
  disableForTask(gid: string): void
  /** Clear the opt-out flag (called when task leaves error state). */
  clearTask(gid: string): void
  /** Convenience: is a task currently mid-cycle (awaiting/backoff/in-flight). */
  isActive(gid: string): boolean
  /** Convenience: how many seconds until the next attempt (0 if n/a). */
  secondsUntilNext(gid: string): number
}

export function useAutoRetry(): UseAutoRetryApi {
  ensureTicker()
  const toast = useToast()
  const { t } = useI18n()

  function updateConfig(patch: Partial<AutoRetryConfig>): void {
    if (typeof patch.enabled === 'boolean') config.enabled = patch.enabled
    if (patch.maxRetries !== undefined) {
      config.maxRetries = clampInt(patch.maxRetries, DEFAULTS.maxRetries, RANGES.maxRetries.min, RANGES.maxRetries.max)
    }
    if (patch.backoffSeconds !== undefined) {
      config.backoffSeconds = clampInt(
        patch.backoffSeconds,
        DEFAULTS.backoffSeconds,
        RANGES.backoffSeconds.min,
        RANGES.backoffSeconds.max,
      )
    }
    if (patch.confirmWindowSeconds !== undefined) {
      config.confirmWindowSeconds = clampInt(
        patch.confirmWindowSeconds,
        DEFAULTS.confirmWindowSeconds,
        RANGES.confirmWindowSeconds.min,
        RANGES.confirmWindowSeconds.max,
      )
    }
    persistConfig()
  }

  async function performRetry(gid: string, name: string): Promise<void> {
    const st = ensureState(gid, name)
    st.phase = 'in-flight'
    st.confirmToastId = null
    try {
      await apiRetry(gid)
      // Success — clear state so future errors start a fresh cycle.
      toast.success(t('autoRetry.toast.success', { name: st.name || gid }))
      clearState(gid)
    } catch (err) {
      st.lastError = err instanceof Error ? err.message : String(err)
      const nextAttempt = st.attempts + 1
      st.attempts = nextAttempt
      if (nextAttempt >= config.maxRetries) {
        st.phase = 'exhausted'
        st.nextAttemptAt = 0
        toast.error(
          t('autoRetry.toast.exhausted', { name: st.name || gid, n: config.maxRetries }),
          st.lastError,
        )
        // Keep state around so the detail panel can show "exhausted" briefly;
        // the next handleError (if backend sends another error) will reset it.
      } else {
        scheduleBackoff(gid)
      }
    }
  }

  function scheduleBackoff(gid: string): void {
    const st = states.get(gid)
    if (!st) return
    const delayMs = config.backoffSeconds * 1000
    st.phase = 'backoff'
    st.nextAttemptAt = Date.now() + delayMs
    clearActiveTimer(gid)
    const handle = window.setTimeout(() => {
      activeTimers.delete(gid)
      // Re-check task is still tracked and not opted out (race-safe).
      const cur = states.get(gid)
      if (!cur || cur.optedOut) return
      void performRetry(gid, cur.name)
    }, delayMs)
    activeTimers.set(gid, handle)
  }

  async function retryNow(gid: string): Promise<void> {
    const st = states.get(gid)
    if (!st) return
    clearActiveTimer(gid)
    await performRetry(gid, st.name)
  }

  function cancelRetry(gid: string): void {
    const st = states.get(gid)
    if (!st) return
    if (st.confirmToastId) {
      toast.dismiss(st.confirmToastId)
    }
    st.optedOut = true
    st.phase = 'cancelled'
    st.nextAttemptAt = 0
    clearActiveTimer(gid)
    skipSet.add(gid)
    persistSkip()
  }

  function disableForTask(gid: string): void {
    cancelRetry(gid)
  }

  function handleError(task: Pick<TaskInfo, 'gid' | 'name'> & Partial<TaskInfo>): void {
    const gid = task.gid
    if (!gid) return
    const name = (task.name && String(task.name)) || gid

    // Master disabled → do nothing (existing manual retry still works).
    if (!config.enabled) return

    // User opted out for this task → ignore.
    if (skipSet.has(gid)) {
      // Clear the opt-out once we leave the error state so the next error
      // gets a fresh prompt.
      return
    }

    // Already mid-cycle for this gid → don't double-prompt.
    const existing = states.get(gid)
    if (existing && (existing.phase === 'awaiting-confirm' || existing.phase === 'backoff' || existing.phase === 'in-flight')) {
      return
    }

    // Fresh error cycle — reset state.
    if (existing) {
      existing.attempts = 0
      existing.optedOut = false
      existing.phase = 'idle'
      existing.startedAt = Date.now()
      existing.lastError = ''
      existing.nextAttemptAt = 0
      existing.confirmToastId = null
    }

    const st = ensureState(gid, name)
    st.phase = 'awaiting-confirm'

    const promptId = toast.warning(
      t('autoRetry.toast.confirmTitle', { name }),
      t('autoRetry.toast.confirmBody', { seconds: config.confirmWindowSeconds }),
      { duration: config.confirmWindowSeconds * 1000 + 500 },
    )
    st.confirmToastId = promptId

    // Schedule auto-yes after the configured confirmation window.
    const handle = window.setTimeout(() => {
      activeTimers.delete(gid)
      const cur = states.get(gid)
      if (!cur || cur.optedOut || cur.phase !== 'awaiting-confirm') return
      // User didn't act in time → count it as accepted and retry.
      cur.attempts += 1
      void performRetry(gid, cur.name)
    }, config.confirmWindowSeconds * 1000)
    activeTimers.set(gid, handle)
  }

  function clearTask(gid: string): void {
    skipSet.delete(gid)
    persistSkip()
    clearState(gid)
  }

  function isActive(gid: string): boolean {
    const st = states.get(gid)
    if (!st) return false
    return st.phase === 'awaiting-confirm' || st.phase === 'backoff' || st.phase === 'in-flight'
  }

  function secondsUntilNext(gid: string): number {
    const st = states.get(gid)
    if (!st) return 0
    if (st.phase === 'backoff' && st.nextAttemptAt > 0) {
      const ms = st.nextAttemptAt - nowTick.value
      return ms > 0 ? Math.ceil(ms / 1000) : 0
    }
    return 0
  }

  const active = computed<RetryTaskState[]>(() => {
    // Touch nowTick so consumers recompute the list each second.
    void nowTick.value
    const list: RetryTaskState[] = []
    states.forEach((st) => {
      if (st.phase !== 'idle') list.push(st)
    })
    return list
  })

  return {
    config,
    states,
    active,
    nowTick: computed(() => nowTick.value),
    skipSet,
    updateConfig,
    handleError,
    retryNow,
    cancelRetry,
    disableForTask,
    clearTask,
    isActive,
    secondsUntilNext,
  }
}

export const AUTO_RETRY_STORAGE_KEY = STORAGE_KEY
export const AUTO_RETRY_DEFAULTS = { ...DEFAULTS }
export const AUTO_RETRY_RANGES = RANGES
